import { Box, Button, Text, useToast } from '@chakra-ui/react'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { TableBuildActionsType } from 'components/organisms/TableBuild/type'
import { InstitutionsResponseDTO } from 'models'
import { useEffect, useRef, useState } from 'react'
import InstitutionsService from 'services/institutions'
import formatter from 'utils/formatter'
import ModalAttachments, {
    RefModalAttachments
} from './components/ModalAttachments'
import { defaultTableHeaders, tableLoaders } from './constants'
import { DocumentType, IAttachments } from './interfaces'

interface PropsAttachments {
    initial: InstitutionsResponseDTO
}

export function Attachments({ initial }: PropsAttachments) {
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const [loadingTable, setLoadingTable] = useState(true)
    const [totalPages, setTotalPages] = useState(1)
    const deleteModalRef = useRef<ModalBuildHandle>(null)
    const [attachmentToDelete, setAttachmentToDelete] = useState<string>('')
    const refModalAttachment = useRef<RefModalAttachments>(null)

    const toast = useToast()

    const onCloseConfirmModal = (): void => {
        deleteModalRef.current?.onClose()
    }

    const handleRemoveFile = () => {
        fetchRemoveAttachment()
    }

    const onCancelDeleteModal = () => {
        onCloseConfirmModal()
    }

    const onOpenConfirmModal = (id: string): void => {
        setAttachmentToDelete(id)
        deleteModalRef.current?.onOpen()
    }

    const handleAction = (type: TableBuildActionsType, id: number | string) => {
        const idAttachment = String(id)

        switch (type) {
            case 'DELETE':
                onOpenConfirmModal(idAttachment)
                break

            case 'DOWNLOAD':
                downloadAttachment(
                    idAttachment,
                    findNameAttachmentById(idAttachment) as string
                )
                break
        }
    }

    const [table, setTable] = useState<
        TableBuildProps<'nomeArquivo' | 'tipoDocumento' | 'dataCriacao'>
    >({
        header: defaultTableHeaders as any,
        action: [
            {
                type: 'DELETE'
            },
            {
                type: 'DOWNLOAD'
            }
        ]
    })

    const onUploadAttachment = () => {
        refModalAttachment.current?.onOpenModal()
    }

    const handlePage = (value: number) => {
        setPage(value)
        setLoadingTable(true)
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setLoadingTable(true)
    }

    const DocumentTypeLabels: { [key in DocumentType]: string } = {
        [DocumentType.CARTAO_CNPJ]: 'Cartão CNPJ',
        [DocumentType.CONTRATO_SOCIAL]: 'Contrato Social',
        [DocumentType.ATA_SOCIETARIA]: 'Ata Societária',
        [DocumentType.DOCUMENTO_SOCIOS]: 'Documento Sócios',
        [DocumentType.COMPROVANTE_ENDERECO]: 'Comprovante de Endereço',
        [DocumentType.OUTROS]: 'Outros'
    }

    const fetchDataList = async () => {
        setLoadingTable(true)
        try {
            const { data: attachments, headers } =
                await InstitutionsService.getAttachmentsByInstitutions(
                    initial?.id,
                    {
                        pagina: page,
                        limite: limit
                    }
                )

            setTotalPages(+headers['total-pages'])
            setTable(old => ({
                ...old,
                body: attachments.map((attachment: IAttachments) => ({
                    ...attachment,
                    tipoDocumento: DocumentTypeLabels[attachment.tipoDocumento],
                    dataCriacao:
                        formatter.dataWithHour(attachment.dataCriacao) ?? ''
                }))
            }))
        } finally {
            setLoadingTable(false)
        }
    }

    const findNameAttachmentById = (id: string) =>
        table?.body?.find(item => item.id === id)?.nomeArquivo ?? 'file'

    const downloadAttachment = async (idAttachment: string, name: string) => {
        setLoadingTable(true)

        try {
            const response =
                await InstitutionsService.downloadAttachmentsByInstitutions(
                    idAttachment
                )
            const data =
                'data:' +
                response.headers['content-type'] +
                ';base64,' +
                response.data

            const link = document.createElement('a')
            link.href = data as string
            link.setAttribute('download', name)
            document.body.appendChild(link)
            link.click()
            link.parentNode?.removeChild(link)
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingTable(false)
        }
    }

    const fetchRemoveAttachment = async () => {
        try {
            await InstitutionsService.deleteAttachmentsByInstitutions(
                initial?.id,
                attachmentToDelete
            )

            toast({
                title: 'Anexo',
                description: 'Anexo excluído com sucesso!',
                status: 'success'
            })

            onCloseConfirmModal()
            await fetchDataList()
        } catch (error) {
            toast({
                title: 'Anexo',
                description:
                    'Falha ao excluir anexo, tente novamente mais tarde!',
                status: 'error'
            })
        }
    }

    const onLoadAttachments = () => {
        fetchDataList()
    }

    useEffect(() => {
        fetchDataList()
    }, [])

    useEffect(() => {
        fetchDataList()
    }, [page, limit])

    return (
        <>
            <Box padding="12px" display={'flex'} justifyContent={'flex-end'}>
                <Button colorScheme="primary" onClick={onUploadAttachment}>
                    Upload Anexo
                </Button>
            </Box>
            <TableBuild
                header={table.header}
                body={table.body}
                action={table.action}
                loading={{
                    status: loadingTable,
                    loader: tableLoaders
                }}
                pagination={{
                    page,
                    limit,
                    totalPages
                }}
                onAction={handleAction}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            />

            <ModalAttachments
                onLoadAttachments={onLoadAttachments}
                initial={initial}
                ref={refModalAttachment}
            />

            <ModalBuild
                ref={deleteModalRef}
                closeOnOverlayClick={false}
                title="Confirmação de exclusão"
                size="md"
                actions={{
                    position: 'flex-end',
                    childrens: [
                        {
                            text: 'Cancelar',
                            handle: 'cancel',
                            variant: 'outline'
                        },
                        {
                            text: 'Deletar',
                            handle: 'confirm',
                            colorScheme: 'red'
                        }
                    ]
                }}
                onConfirm={handleRemoveFile}
                onCancel={onCancelDeleteModal}
            >
                <Text px={6} py={4}>
                    Você realmente deseja deletar? Uma vez deletado, não será
                    possível voltar atrás.
                </Text>
            </ModalBuild>
        </>
    )
}
